import { useState, useMemo, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useOptimizerStore } from "../../stores/optimizerStore";
import { useFeatureFlagStore } from "../../stores/featureFlagStore";
import { isAdmin } from "../../services/authService";
import { ThemeSelector } from "./ThemeSelector";
import { MarketStatusBanner } from "../MarketStatusBanner";

// Reordered: Pulse, Browse, Subscription, Brokerage, Portfolio, Watchlist, Backtest, FAQ
const navItems = [
  {
    path: "/pulse",
    label: "Pulse",
    icon: "M13 10V3L4 14h7v7l9-11h-7z",
    description: "Market intelligence",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/search",
    label: "Browse",
    icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
    description: "Explore strategies",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/subscription",
    label: "Subscription",
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
    description: "Plan & billing",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/brokerage",
    label: "Brokerage",
    icon: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z",
    description: "Connect Alpaca",
    adminOnly: false,
    authRequired: true,
  },
  {
    path: "/portfolio",
    label: "Portfolio",
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    description: "Your holdings",
    adminOnly: false,
    authRequired: true,
  },
  {
    path: "/bookmarks",
    label: "Watchlist",
    icon: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z",
    description: "Saved strategies",
    adminOnly: false,
    authRequired: true,
  },
  {
    path: "/backtest",
    label: "Backtest",
    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
    description: "Run simulations",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/optimizer/jobs",
    label: "Optimizer",
    icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
    description: "AI strategy evolution",
    adminOnly: false,
    authRequired: true,
  },
  {
    path: "/walkthroughs",
    label: "Walkthroughs",
    icon: "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    description: "Product walkthroughs",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/diversifier",
    label: "Diversifier",
    icon: "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm12 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z",
    description: "Portfolio scenarios",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/portfolio-lab",
    label: "Portfolio Analyzer",
    icon: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
    description: "Correlation & allocation",
    adminOnly: false,
    authRequired: true,
  },
  {
    path: "/builder",
    label: "Builder",
    icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
    description: "Visual strategy editor",
    adminOnly: false,
    authRequired: true,
  },
  {
    path: "/faq",
    label: "FAQ",
    icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    description: "Common questions",
    adminOnly: false,
    authRequired: false,
  },
  {
    path: "/admin/subscriptions",
    label: "Subscriptions",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
    description: "Manage subs",
    adminOnly: true,
    authRequired: true,
  },
  {
    path: "/settings",
    label: "Settings",
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    description: "Cache & config",
    adminOnly: true,
    authRequired: true,
  },
];

interface SidebarProps {
  variant?: "desktop" | "mobile";
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({
  variant = "desktop",
  isOpen = false,
  onClose,
}: SidebarProps) {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const runningJobCount = useOptimizerStore((s) => s.runningJobCount);
  const fetchRunningCount = useOptimizerStore((s) => s.fetchRunningCount);
  const flags = useFeatureFlagStore((s) => s.flags);

  const isMobile = variant === "mobile";

  // Poll for optimizer running job count every 30s
  useEffect(() => {
    if (isAuthenticated) {
      fetchRunningCount();
      const interval = setInterval(() => fetchRunningCount(), 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchRunningCount]);

  // Filter nav items based on user role and auth status
  const filteredNavItems = useMemo(() => {
    return navItems.filter((item) => {
      // Hide optimizer when feature is disabled
      if (item.path === "/optimizer/jobs" && !flags.OPTIMIZER) {
        return false;
      }
      // Hide video catalog when feature is disabled
      if (item.path === "/walkthroughs" && !flags.VIDEO_CATALOG) {
        return false;
      }
      // Admin-only items require admin role
      if (item.adminOnly) {
        return isAdmin(user);
      }
      // Auth-required items only show when logged in
      if (item.authRequired && !isAuthenticated) {
        return false;
      }
      return true;
    });
  }, [user, isAuthenticated, flags]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleClose = () => {
    onClose?.();
  };

  const containerClasses = isMobile
    ? `fixed inset-y-0 left-0 z-50 bg-surface-900/95 backdrop-blur-xl border-r border-surface-800/50 flex flex-col transition-transform duration-300 ease-in-out w-80 max-w-[85vw] h-[100dvh] md:hidden ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`
    : `hidden md:flex h-screen bg-surface-900/95 backdrop-blur-xl border-r border-surface-800/50 flex-col transition-all duration-300 ease-in-out ${
        isCollapsed ? "w-20" : "w-64"
      }`;

  return (
    <div className={containerClasses}>
      {/* Header with Colvix logo and TradeCraft branding */}
      <div className="p-4 border-b border-surface-800/50">
        {/* Mobile close button */}
        {isMobile && (
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-8 h-8" />
              <div>
                <h2 className="text-sm font-semibold text-surface-100">
                  TradeCraft
                </h2>
                <p className="text-xs text-surface-400">Algo Builder</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close navigation"
              onClick={handleClose}
              className="p-2 rounded-lg bg-surface-800/60 border border-surface-700/50 text-surface-200"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Colvix logo - links to colvix.net */}
        <a
          href="https://www.colvix.net"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center mb-3 opacity-80 hover:opacity-100 transition-opacity"
        >
          <img
            src="/colvix_header_final.png"
            alt="Colvix"
            className={`colvix-logo transition-all duration-300 ${!isMobile && isCollapsed ? "h-6" : "h-8"}`}
          />
        </a>

        {/* TradeCraft branding */}
        <div
          className={`flex items-center gap-3 ${!isMobile && isCollapsed ? "justify-center" : ""} ${isMobile ? "hidden" : ""}`}
        >
          <img
            src="/logo.svg"
            alt="TradeCraft"
            className="w-10 h-10 flex-shrink-0"
          />
          {!isCollapsed && (
            <div className="animate-fade-in">
              <h1 className="text-lg font-bold gradient-text">TradeCraft</h1>
              <p className="text-xs text-surface-400">Algo Builder</p>
            </div>
          )}
        </div>
      </div>

      {/* Collapse toggle button - modern pill design */}
      {!isMobile && (
        <button
          onClick={toggleCollapse}
          className={`mx-3 mt-4 py-2 px-3 rounded-full bg-gradient-to-r from-surface-800/80 to-surface-800/40 hover:from-surface-700/80 hover:to-surface-700/40 border border-surface-700/30 hover:border-surface-600/50 transition-all duration-300 flex items-center justify-center gap-2 group shadow-lg shadow-surface-950/50 ${isCollapsed ? "" : ""}`}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <div
            className={`flex items-center justify-center transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`}
          >
            <svg
              className="w-4 h-4 text-surface-400 group-hover:text-primary-400 transition-colors duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </div>
          {!isCollapsed && (
            <span className="text-xs text-surface-500 group-hover:text-surface-300 transition-colors">
              Collapse
            </span>
          )}
        </button>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1.5 mt-2 overflow-y-auto sidebar-scrollbar">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            title={isCollapsed ? item.label : undefined}
            onClick={() => {
              if (isMobile) handleClose();
            }}
            className={({ isActive }) =>
              `group flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 no-underline relative overflow-hidden ${
                isActive
                  ? "bg-primary-500/15 text-primary-400 border border-primary-500/30"
                  : "text-surface-300 hover:text-surface-100 hover:bg-surface-800/50 border border-transparent"
              } ${isCollapsed ? "justify-center" : ""}`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-transparent pointer-events-none" />
                )}
                <svg
                  className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${
                    isActive ? "text-primary-400" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={item.icon}
                  />
                </svg>
                {!isCollapsed && (
                  <div className="flex flex-col animate-fade-in">
                    <span className="font-medium text-sm">{item.label}</span>
                    <span className="text-xs text-surface-500">
                      {item.description}
                    </span>
                  </div>
                )}
                {!isCollapsed &&
                  item.path === "/optimizer/jobs" &&
                  runningJobCount > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30 animate-pulse">
                      {runningJobCount}
                    </span>
                  )}
              </>
            )}
          </NavLink>
        ))}

        {/* Separator */}
        <div className="my-3 border-t border-surface-700/50" />

        {/* Support link */}
        <NavLink
          to="/support"
          title={isCollapsed ? "Support" : undefined}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
              isActive
                ? "bg-brand-500/20 border border-brand-500/30 text-brand-400"
                : "bg-surface-800/30 hover:bg-surface-700/50 text-surface-300 hover:text-brand-400 border border-transparent hover:border-surface-600/30"
            } ${isCollapsed ? "justify-center" : ""}`
          }
        >
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          {!isCollapsed && (
            <div className="flex flex-col animate-fade-in">
              <span className="font-medium text-sm">Support</span>
              <span className="text-xs text-surface-500">Get help</span>
            </div>
          )}
        </NavLink>

        {/* Readme link */}
        <a
          href="https://www.colvix.net/blog/tradecraft-ai-algorithmic-strategy-backtesting-platform"
          title={isCollapsed ? "Readme" : undefined}
          onClick={() => {
            if (isMobile) handleClose();
          }}
          className={`group flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 no-underline relative overflow-hidden text-surface-300 hover:text-surface-100 hover:bg-surface-800/50 border border-transparent ${isCollapsed ? "justify-center" : ""}`}
        >
          <svg
            className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          {!isCollapsed && (
            <div className="flex flex-col animate-fade-in">
              <span className="font-medium text-sm">Readme</span>
              <span className="text-xs text-surface-500">Documentation</span>
            </div>
          )}
        </a>
      </nav>

      {/* Market Status */}
      {!isCollapsed && (
        <div className="px-3 pb-3">
          <MarketStatusBanner />
        </div>
      )}

      {/* Theme Selector */}
      <div className={`px-3 pb-2 ${isCollapsed ? "flex justify-center" : ""}`}>
        <ThemeSelector isCollapsed={!isMobile && isCollapsed} variant="compact" />
      </div>

      {/* User section */}
      <div className="p-3 border-t border-surface-800/50">
        {isAuthenticated ? (
          <>
            <NavLink
              to="/profile"
              title={isCollapsed ? "Profile" : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 mb-3 p-2 rounded-xl transition-all duration-200 cursor-pointer ${
                  isActive
                    ? "bg-primary-500/20 border border-primary-500/30"
                    : "bg-surface-800/30 hover:bg-surface-700/50 border border-transparent hover:border-surface-600/30"
                } ${isCollapsed ? "justify-center" : ""}`
              }
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-sm">
                  {user?.display_name?.[0]?.toUpperCase() || "U"}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0 animate-fade-in">
                  <p className="text-sm font-medium text-surface-100 truncate">
                    {user?.display_name || "User"}
                  </p>
                  <p className="text-xs text-surface-400 truncate">
                    {user?.email || ""}
                  </p>
                </div>
              )}
            </NavLink>
            <button
              onClick={handleLogout}
              title={isCollapsed ? "Sign Out" : undefined}
              className={`w-full py-2.5 px-3 rounded-lg bg-surface-800/50 hover:bg-red-500/20 border border-surface-700/30 hover:border-red-500/30 text-surface-300 hover:text-red-400 transition-all duration-200 flex items-center gap-2 text-sm font-medium ${
                isCollapsed ? "justify-center" : ""
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {!isCollapsed && <span>Sign Out</span>}
            </button>
          </>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => navigate("/login")}
              className={`w-full py-2.5 px-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-all duration-200 flex items-center gap-2 text-sm font-medium justify-center`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
              {!isCollapsed && <span>Sign In</span>}
            </button>
            <button
              onClick={() => navigate("/signup")}
              className={`w-full py-2.5 px-3 rounded-lg bg-surface-800/50 hover:bg-surface-700/50 border border-surface-700/30 hover:border-surface-600/50 text-surface-300 hover:text-surface-100 transition-all duration-200 flex items-center gap-2 text-sm font-medium justify-center`}
            >
              {!isCollapsed && <span>Create Account</span>}
              {isCollapsed && (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
